import { useCallback, useMemo, useState } from 'react';

const EMPTY_SELECTION = {
  selectedPinId: null,
  selectedPinIds: [],
  selectedLinkId: null,
  selectedLinkIds: [],
  selectedShapeId: null,
  selectedShapeIds: [],
  selectedRegionId: null,
  selectedRegionIds: [],
  selectedDrawingId: null,
  selectedDrawingIds: [],
  selectedWidgetId: null,
  selectedWidgetIds: [],
  selectedGroupId: null,
  selectedGroupIds: [],
};

export const useBoardMapSelection = () => {
  const [selection, setSelection] = useState(EMPTY_SELECTION);

  const clearSelection = useCallback(() => {
    setSelection(EMPTY_SELECTION);
  }, []);

  const selectPinOnly = useCallback(pinId => {
    setSelection({
      ...EMPTY_SELECTION,
      selectedPinId: pinId || null,
      selectedPinIds: pinId ? [pinId] : [],
    });
  }, []);

  const selectPins = useCallback(pinIds => {
    const nextPinIds = Array.isArray(pinIds)
      ? [...new Set(pinIds.filter(Boolean))]
      : [];
    setSelection({
      ...EMPTY_SELECTION,
      selectedPinId: nextPinIds.length === 1 ? nextPinIds[0] : null,
      selectedPinIds: nextPinIds,
    });
  }, []);

  const selectLinkOnly = useCallback(linkId => {
    setSelection({
      ...EMPTY_SELECTION,
      selectedLinkId: linkId || null,
      selectedLinkIds: linkId ? [linkId] : [],
    });
  }, []);

  const selectShapeOnly = useCallback(shapeId => {
    setSelection({
      ...EMPTY_SELECTION,
      selectedShapeId: shapeId || null,
      selectedShapeIds: shapeId ? [shapeId] : [],
    });
  }, []);

  const selectShapes = useCallback(shapeIds => {
    const nextShapeIds = Array.isArray(shapeIds)
      ? [...new Set(shapeIds.filter(Boolean))]
      : [];
    setSelection({
      ...EMPTY_SELECTION,
      selectedShapeId: nextShapeIds.length === 1 ? nextShapeIds[0] : null,
      selectedShapeIds: nextShapeIds,
    });
  }, []);

  const selectRegionOnly = useCallback(regionId => {
    setSelection({
      ...EMPTY_SELECTION,
      selectedRegionId: regionId || null,
      selectedRegionIds: regionId ? [regionId] : [],
    });
  }, []);

  const selectRegions = useCallback(regionIds => {
    const nextRegionIds = Array.isArray(regionIds)
      ? [...new Set(regionIds.filter(Boolean))]
      : [];
    setSelection({
      ...EMPTY_SELECTION,
      selectedRegionId: nextRegionIds.length === 1 ? nextRegionIds[0] : null,
      selectedRegionIds: nextRegionIds,
    });
  }, []);

  const selectDrawingOnly = useCallback(drawingId => {
    setSelection({
      ...EMPTY_SELECTION,
      selectedDrawingId: drawingId || null,
      selectedDrawingIds: drawingId ? [drawingId] : [],
    });
  }, []);

  const selectDrawings = useCallback(drawingIds => {
    const nextDrawingIds = Array.isArray(drawingIds)
      ? [...new Set(drawingIds.filter(Boolean))]
      : [];
    setSelection({
      ...EMPTY_SELECTION,
      selectedDrawingId: nextDrawingIds.length === 1 ? nextDrawingIds[0] : null,
      selectedDrawingIds: nextDrawingIds,
    });
  }, []);

  const selectWidgetOnly = useCallback(widgetId => {
    setSelection({
      ...EMPTY_SELECTION,
      selectedWidgetId: widgetId || null,
      selectedWidgetIds: widgetId ? [widgetId] : [],
    });
  }, []);

  const selectWidgets = useCallback(widgetIds => {
    const nextWidgetIds = Array.isArray(widgetIds)
      ? [...new Set(widgetIds.filter(Boolean))]
      : [];
    setSelection({
      ...EMPTY_SELECTION,
      selectedWidgetId: nextWidgetIds.length === 1 ? nextWidgetIds[0] : null,
      selectedWidgetIds: nextWidgetIds,
    });
  }, []);

  const selectGroupOnly = useCallback(groupId => {
    setSelection({
      ...EMPTY_SELECTION,
      selectedGroupId: groupId || null,
      selectedGroupIds: groupId ? [groupId] : [],
    });
  }, []);

  const setSelectionState = useCallback(nextSelection => {
    setSelection(prev => ({ ...prev, ...nextSelection }));
  }, []);

  return useMemo(
    () => ({
      ...selection,
      clearSelection,
      selectPinOnly,
      selectPins,
      selectLinkOnly,
      selectShapeOnly,
      selectShapes,
      selectRegionOnly,
      selectRegions,
      selectDrawingOnly,
      selectDrawings,
      selectWidgetOnly,
      selectWidgets,
      selectGroupOnly,
      setSelectionState,
    }),
    [
      selection,
      clearSelection,
      selectPinOnly,
      selectPins,
      selectLinkOnly,
      selectShapeOnly,
      selectShapes,
      selectRegionOnly,
      selectRegions,
      selectDrawingOnly,
      selectDrawings,
      selectWidgetOnly,
      selectWidgets,
      selectGroupOnly,
      setSelectionState,
    ],
  );
};
